import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Sparkles, Users2, Clock, TrendingUp, AlarmClock, TriangleAlert } from "lucide-react";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;

  const toArr = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name: name.replaceAll("_", " "), value }));

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Workload, Quality & Performance</h1>
        <p className="text-muted-foreground">Management view — responsiveness, capacity, and human-control metrics.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label="AI Accept Rate" value={`${d.human_control.accept_rate}%`} sub={`${d.human_control.reviewed} reviewed`} />
        <KPI icon={Sparkles} label="Overridden / Modified" value={d.human_control.modified_or_overridden} sub="human judgment applied" />
        <KPI icon={Clock} label="Awaiting Feedback" value={d.pending_feedback} sub="chapters pending" />
        <KPI icon={Users2} label="Active Supervisions" value={d.totals.active_supervisions} sub={`${d.totals.chapters} chapters`} />
      </div>

      {d.turnaround_leaderboard && (
        <Card className="p-6" data-testid="turnaround-leaderboard">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <AlarmClock className="h-4 w-4 brand-text" />
              <h3 className="font-heading font-semibold">Review Turnaround Leaderboard</h3>
            </div>
            <span className="text-xs text-muted-foreground">Overdue threshold: {d.sla_days} days</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Supervisors ranked by ageing backlog — chapters currently awaiting a decision.</p>
          {d.turnaround_leaderboard.length === 0 ? (
            <p className="text-sm text-muted-foreground">No chapters are awaiting review right now.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="turnaround-table">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Supervisor</th>
                    <th className="py-2 px-4 font-medium text-right">Awaiting</th>
                    <th className="py-2 px-4 font-medium text-right">Overdue</th>
                    <th className="py-2 px-4 font-medium text-right">Avg wait</th>
                    <th className="py-2 pl-4 font-medium text-right">Oldest</th>
                  </tr>
                </thead>
                <tbody>
                  {d.turnaround_leaderboard.map((r) => (
                    <tr key={r.name} className="border-b border-border/60" data-testid={`turnaround-row-${r.name}`}>
                      <td className="py-2 pr-4 font-medium text-foreground">{r.name}</td>
                      <td className="py-2 px-4 text-right">{r.awaiting}</td>
                      <td className="py-2 px-4 text-right">
                        {r.overdue > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><TriangleAlert className="h-3 w-3" />{r.overdue}</span>
                        ) : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-2 px-4 text-right">{r.avg_days}d</td>
                      <td className={`py-2 pl-4 text-right ${r.oldest_days >= d.sla_days ? "text-red-700 font-semibold" : ""}`}>{r.oldest_days}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <h3 className="font-heading font-semibold mb-4">Concept-Paper Pipeline</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={toArr(d.concept_status)}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="name" fontSize={11} tickLine={false} />
              <YAxis fontSize={11} allowDecimals={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-semibold mb-4">Supervision Stages</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={toArr(d.supervision_stage)} dataKey="value" nameKey="name" outerRadius={90} label>
                {toArr(d.supervision_stage).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-semibold mb-4">Supervisor Workload</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.supervisor_load} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
              <XAxis type="number" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="name" fontSize={11} width={110} />
              <Tooltip />
              <Bar dataKey="load" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-semibold mb-4">Application Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={toArr(d.application_status)}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="name" fontSize={11} tickLine={false} />
              <YAxis fontSize={11} allowDecimals={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="value" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5">
      <Icon className="h-5 w-5 brand-text" />
      <p className="text-3xl font-heading font-bold mt-2">{value}</p>
      <p className="text-sm text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </Card>
  );
}
